import { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { customerState } from '@/app/auth/auth.state'
import config from '@/config'
import { trackEvent } from './tracking'

/**
 * Firebase-Remote-Config-driven rollout of the AI appraisal flow
 * (/wert-check).
 *
 * The boolean parameter AI_APPRAISAL_ENABLED_PARAMETER decides per visitor.
 * All targeting lives in the Firebase console as conditions on that
 * parameter, evaluated top-down:
 * - force lists on the custom signals `cashy_customer_id` /
 *   `cashy_email_domain` (set for logged-in customers, see
 *   AiAppraisalSignals) to pin specific users into either variant
 * - a percent condition for the gradual rollout — sticky per browser via
 *   the Firebase installation id, and monotonic: raising the percentage
 *   only ever adds visitors
 *
 * Overrides, in order of precedence:
 * - AI_APPRAISAL_ENABLED env (dev): always on, no Firebase needed.
 * - localStorage cashy_ai_appraisal_override = 'on' | 'off' (manual QA,
 *   works for anonymous visitors).
 */

export const AI_APPRAISAL_ENABLED_PARAMETER = 'ai_appraisal_enabled'

const OVERRIDE_STORAGE_KEY = 'cashy_ai_appraisal_override'
const MINIMUM_FETCH_INTERVAL_MS = 5 * 60 * 1000

export function isAiAppraisalRemotelyConfigurable(): boolean {
  return Boolean(config.firebaseWebAppConfig)
}

function getManualOverride(): boolean | undefined {
  try {
    const value = window.localStorage.getItem(OVERRIDE_STORAGE_KEY)
    if (value === 'on') return true
    if (value === 'off') return false
  } catch {
    // ignore
  }
  return undefined
}

// Dynamic imports keep firebase out of the server bundle — remote-config
// is browser-only (IndexedDB).
async function getRemoteConfigInstance() {
  const options = JSON.parse(config.firebaseWebAppConfig)
  const { getApp, getApps, initializeApp } = await import('firebase/app')
  const { getRemoteConfig } = await import('firebase/remote-config')

  const app = getApps().length ? getApp() : initializeApp(options)
  const remoteConfig = getRemoteConfig(app)
  remoteConfig.settings.minimumFetchIntervalMillis = MINIMUM_FETCH_INTERVAL_MS
  remoteConfig.defaultConfig = { [AI_APPRAISAL_ENABLED_PARAMETER]: false }
  return remoteConfig
}

let resolvePromise: Promise<boolean> | undefined

async function resolveEnabled(): Promise<boolean> {
  if (config.aiAppraisalEnabled) {
    return true
  }

  const override = getManualOverride()
  if (override !== undefined) {
    return override
  }

  if (!isAiAppraisalRemotelyConfigurable()) {
    return false
  }

  const { fetchAndActivate, getBoolean } = await import(
    'firebase/remote-config'
  )
  const remoteConfig = await getRemoteConfigInstance()
  await fetchAndActivate(remoteConfig)
  const enabled = getBoolean(remoteConfig, AI_APPRAISAL_ENABLED_PARAMETER)

  trackEvent('ai_appraisal_cohort', {
    cohort: enabled ? 'ai_wert_check' : 'catalogue',
  })
  return enabled
}

/** Resolved at most once per page load, shared by all consumers. */
export function isAiAppraisalEnabled(): Promise<boolean> {
  if (!resolvePromise) {
    resolvePromise = resolveEnabled().catch((error) => {
      console.error('Failed to resolve AI appraisal toggle', error)
      resolvePromise = undefined
      return false
    })
  }
  return resolvePromise
}

/**
 * `undefined` while the remote value is being fetched, then the decision.
 * With the AI_APPRAISAL_ENABLED env override it is `true` immediately.
 */
export function useAiAppraisalToggle(): boolean | undefined {
  const [enabled, setEnabled] = useState<boolean | undefined>(
    config.aiAppraisalEnabled || undefined,
  )

  useEffect(() => {
    let cancelled = false
    isAiAppraisalEnabled().then((value) => {
      if (!cancelled) setEnabled(value)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return enabled
}

/**
 * Mirrors the logged-in customer into Remote Config custom signals so
 * console conditions can force specific users into a variant. Signals only
 * apply from the NEXT config fetch (at most every 5 minutes), so a
 * force-listed user may need a reload after a few minutes to flip.
 * Mounted once in _app inside the RecoilRoot.
 */
export function AiAppraisalSignals(): null {
  const customer = useRecoilValue(customerState)
  const customerId = customer?._id ?? null
  const emailDomain = customer?.email?.split('@')[1]?.toLowerCase() ?? null

  useEffect(() => {
    if (!isAiAppraisalRemotelyConfigurable()) return

    let cancelled = false
    ;(async () => {
      const { setCustomSignals } = await import('firebase/remote-config')
      const remoteConfig = await getRemoteConfigInstance()
      if (cancelled) return
      // null deletes a signal — logout clears the targeting.
      await setCustomSignals(remoteConfig, {
        cashy_customer_id: customerId,
        cashy_email_domain: emailDomain,
      })
    })().catch((error) => {
      console.error('Failed to set AI appraisal user signals', error)
    })

    return () => {
      cancelled = true
    }
  }, [customerId, emailDomain])

  return null
}
